<template lang="pug">
.send-request-tab
  .mb-3

  .register-body
    FormLabel(key-name="送信対象施設" :value="_targetFacilityNames")

    FormInput(v-model="residentName" key-name="利用者（患者）識別用の名称/番号/記号など" name="resident-name" type="text" placeholder="利用者（患者）識別用の名称/番号/記号など"
      optionText="管理用（他施設には通知されません）"
      :maxlength="32" :validation-rules="{ required: true, max: 32 }" :styles="{ width: '340px' }" required)
    
    FormInputRadio(v-model="addResidentInfo" key-name="利用者（患者）の匿名情報の送信" name="send-message"
      :options="_addResidentInfoOptions" :validation-rules="{ required: true }" required)
    .resident-info.ml-3.mb-4(v-if="addResidentInfo == true")
      .mt-4
      FormInput(v-model="residentInfo.age" key-name="年齢" name="resident-age" type="text" unitText="歳"
        :maxlength="3" :validation-rules="{ custom_numeric: true, max: 3, required: true }" :styles="{ 'max-width': '60px', 'min-width': '60px' }" required)
      FormInputRadio(v-model="residentInfo.gender" key-name="性別" name="gender" :options="_genderOptions"
        :validation-rules="{ required: true }" required)
      FormInputCarelevelsRadio(v-model="residentInfo.careLevel" name="care-level" :validation-rules="{ required: true }" required)
      FormInputRadio(v-model="residentInfo.welfare" key-name="生活保護" name="welfare" :options="_addWelfareOptions" :validation-rules="{ required: true }" required)
      InputAdl(v-model="residentInfo.adl" name="send-request-tab" :validation-rules="{ required: true }" required)
      FormDatePicker(v-model="residentInfo.outDatetime" :date.sync="residentInfo.outDatetimeDate"
        key-name="退所（退院）予定日" name="out-datetime-date" placeholder="退所（退院）予定日")

    FormTextarea(key-name="メッセージ内容（補足説明など）" name="message-text" v-model="messageText"
      placeholder="例："
      :validation-rules="{ max: 1000, required: true }" :maxlength="1000" :styles="{ maxWidth: '640px', width: '640px', minHeight: '160px' }" required)
    FormInputRadio(v-model="replyAcceptance" key-name="入所可否確認" name="need-reply"
      :options="_replyAcceptanceOptions" :validation-rules="{ required: true }" required)

  .box-footer.register-footer(style="padding: 10px 0 0 0;")
    ValidationAlert(v-model="showValidationAlert")
    .buttons.m-0
      button.btn.send-message-btn(type="button" @click="onSendMessage()") 送信
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import CustomValidate from "@/commons/utils/CustomValidate";

import FormLabel from "@/pc/components/forms/FormLabel.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputRadio from "@/pc/components/forms/FormInputRadio.vue";
import FormInputCarelevelsRadio from "@/pc/components/otherOfficeRoomSchedules/FormInputCarelevelsRadio.vue";
import InputAdl, { InputAdlModel } from "@/pc/components/otherOfficeRoomSchedules/InputAdl.vue";
import FormDatePicker from "@/pc/components/forms/FormDatePicker.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import { OtherOfficeAcceptanceRequestsPostModel } from "@/commons/services/OtherOfficeAcceptanceRequests";

/**
 * ### 送信タブコンポーネント
 */
@Component({
  components: {
    FormLabel,
    FormInput,
    FormInputRadio,
    FormInputCarelevelsRadio,
    InputAdl,
    FormDatePicker,
    FormTextarea,
    ValidationAlert
  },
  $_veeValidate: { validator: "new" }
})
export default class SendRequestTab extends Vue {
  @Prop()
  targetFacilities!: Array<{ facilityId: number; facilityName: string }>;

  /** 利用者（患者）名 */
  residentName: string = "";
  /** 生活保護の有無 */
  welfare: boolean | null = null;
  /** 利用者（患者情報）の送信可否 */
  addResidentInfo: boolean | null = null;
  /** 利用者情報 */
  residentInfo: ResidentInfo = {
    age: "",
    gender: "",
    careLevel: "",
    /** 生活保護の有無 */
    welfare: null,
    /** ADL */
    adl: {
      move: null,
      meel: null,
      bathing: null,
      excretion: null
    },
    /** 退所（退院）予定日 */
    outDatetime: null,
    /** 退所（退院）予定日 */
    outDatetimeDate: null
  };
  /** メッセージ内容（補足説明など） */
  messageText: string = "";
  /** 入所可否確認 */
  replyAcceptance: boolean | null = null;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  get _targetFacilityNames(): string {
    if (!this.targetFacilities) {
      return "";
    }
    return this.targetFacilities.map((facility) => facility.facilityName).join("、");
  }

  get _addResidentInfoOptions(): Array<{ value: boolean; label: string }> {
    return [
      { value: true, label: "送信する" },
      { value: false, label: "送信しない" }
    ];
  }

  get _genderOptions(): Array<{ value: string; label: string }> {
    const ageOptions: Array<{ value: string; label: string }> = [
      { label: "男性", value: "男性" },
      { label: "女性", value: "女性" }
    ];
    return ageOptions;
  }

  get _addWelfareOptions(): Array<{ value: boolean; label: string }> {
    return [
      { value: true, label: "有り" },
      { value: false, label: "無し" }
    ];
  }

  get _replyAcceptanceOptions(): Array<{ value: boolean; label: string }> {
    return [
      { value: true, label: "確認する" },
      { value: false, label: "確認しない" }
    ];
  }

  /**
   * vue lifecycle created
   */
  created() {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  async onSendMessage(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      const requestBody: OtherOfficeAcceptanceRequestsPostModel = this.createRequestBody();
      this.$emit("sendRequest", requestBody);
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  private createRequestBody(): OtherOfficeAcceptanceRequestsPostModel {
    let residentInfo: any | null = null;
    // 利用者（患者情報）の送信可否が「送信する」の場合はresidentInfoを作成してAPIに送信する
    // anyにしているのでAPI側が受け付ける型定義と異なりやすいので注意
    if (this.addResidentInfo) {
      residentInfo = {
        age: Number(this.residentInfo.age),
        gender: this.residentInfo.gender as "男性" | "女性",
        careLevel: this.residentInfo.careLevel,
        welfare: this.residentInfo.welfare,
        adl: {
          move: this.residentInfo.adl.move,
          meel: this.residentInfo.adl.meel,
          bathing: this.residentInfo.adl.bathing,
          excretion: this.residentInfo.adl.excretion
        },
        outDatetime: this.residentInfo.outDatetime
      };
    }
    // 利用者（患者情報）の送信可否が「送信しない」の場合はresidentInfoをnullで送信する
    else {
      residentInfo = null;
    }

    // targetFacilitiesからfacilityIdの配列を抽出
    const facilityIds = this.targetFacilities ? this.targetFacilities.map((facility) => facility.facilityId) : [];

    // sendFacilityIds以外は本コンポーネントで入力するので格納して親コンポーネントに渡せるようにする
    const requestBody: OtherOfficeAcceptanceRequestsPostModel = {
      residentName: this.residentName,
      welfare: this.welfare as boolean,
      residentInfo: residentInfo,
      messageText: this.messageText,
      replyAcceptance: this.replyAcceptance as boolean,
      sendFacilityIds: facilityIds
    };
    return requestBody;
  }

  /** ### 入力項目のリセット（親コンポーネントから呼び出される） */
  public initialize(): void {
    this.residentName = "";
    this.addResidentInfo = null;
    this.residentInfo = {
      age: "",
      gender: "",
      careLevel: "",
      /** 生活保護の有無 */
      welfare: null,
      /** ADL */
      adl: {
        move: null,
        meel: null,
        bathing: null,
        excretion: null
      },
      /** 退所（退院）予定日 */
      outDatetime: null,
      /** 退所（退院）予定日 */
      outDatetimeDate: null
    };
    this.messageText = "";
    this.replyAcceptance = null;
    return;
  }
}
export interface ResidentInfo {
  /** 年齢 */
  age: string;
  /** 性別 */
  gender: string;
  /** 介護度 */
  careLevel: string;
  /** 生活保護の有無 */
  welfare: boolean | null;
  /** ADL */
  adl: InputAdlModel;
  /** 退所（退院）予定日 */
  outDatetime: number | null;
  /** 退所（退院）予定日 */
  outDatetimeDate: string | null;
}
</script>

<style lang="scss" scoped>
.send-request-tab {
  .resident-info {
    background-color: #fff;
    border: 1px solid #cccccc;
    border-radius: 0.5rem;

    width: 800px;
    min-width: 800px;
    max-width: 800px;
  }
  .send-message-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
